import React, { useState, useEffect, useRef } from 'react';

/**
 * MasterDropdown Component.
 * Searchable typeahead dropdown component for selecting or typing Bank / Company master entries.
 * Supports exact matching, manual text entry, and adding new records to master list.
 *
 * @component
 * @param {Object} props - React component props.
 * @param {string} [props.value=""] - Current selected or typed input value.
 * @param {Function} props.onChange - Callback fired when value changes (`onChange(value, matchedItem)`).
 * @param {Array<Object>} [props.items=[]] - Master item list (`[{ _id, name, routingNumber, address, ... }]`).
 * @param {string} [props.placeholder="Select or type…"] - Input placeholder text.
 * @param {string} [props.icon="🏢"] - Emoji icon displayed alongside search items.
 * @param {Function} [props.renderBadge] - Optional renderer function returning JSX badge for an item (`renderBadge(item)`).
 * @param {boolean} [props.disabled=false] - Whether dropdown input is disabled.
 * @param {boolean} [props.allowManual=true] - Whether free-text typing is permitted when not in list.
 * @param {Function} [props.onAddNew] - Optional callback triggered when user clicks "Add to Master" option (`onAddNew(name)`).
 * @returns {JSX.Element} MasterDropdown element.
 */
export default function MasterDropdown({
  value = '',
  onChange,
  items = [],
  placeholder = 'Select or type…',
  icon = '🏢',
  renderBadge,
  disabled = false,
  allowManual = true,
  onAddNew,
}) {
  const [open, setOpen]   = useState(false);
  const [query, setQuery] = useState(value);
  const [hov, setHov]     = useState(null);
  const ref               = useRef(null);

  // Sync when parent changes value (e.g. scan autofill)
  // Also auto-select from list if exact match found (case-insensitive)
  useEffect(() => {
    setQuery(value);
    if (value) {
      const match = items.find(i => i.name.toLowerCase() === value.trim().toLowerCase());
      if (match) {
        // Notify parent with the matched item so it has the full object
        onChange(match.name, match);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, items]);

  // Close on outside click
  useEffect(() => {
    const fn = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', fn);
    return () => document.removeEventListener('mousedown', fn);
  }, []);

  const filtered = items.filter(item =>
    item.name.toLowerCase().includes(query.toLowerCase())
  );

  // Exact match (case-insensitive) — true if typed name already in master list
  const exactMatch = items.some(i => i.name.toLowerCase() === query.trim().toLowerCase());

  const select = (item) => {
    setQuery(item.name);
    onChange(item.name, item);
    setOpen(false);
  };

  const handleChange = (e) => {
    setQuery(e.target.value);
    onChange(e.target.value, null);
    setOpen(true);
  };

  // Show "Use as entered" only if allowManual and no exact match and no "Add to Master" option
  const showManual = allowManual && !onAddNew &&
    query.trim() && !exactMatch;

  // Show "Add to Company Master" option if onAddNew provided and name not in list
  const showAddNew = !!onAddNew && query.trim() && !exactMatch;

  // Current item (for selected state styling)
  const selectedItem = items.find(i => i.name.toLowerCase() === query.trim().toLowerCase());

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <div style={{ position: 'relative' }}>
        <input
          style={{
            width: '100%',
            padding: '9px 36px 9px 12px',
            border: open
              ? '2px solid var(--primary)'
              : selectedItem
                ? '1.5px solid var(--primary)'
                : '1px solid var(--gray-3)',
            borderRadius: 8,
            fontSize: 14,
            outline: 'none',
            background: disabled ? 'var(--gray-4)' : selectedItem ? 'var(--primary-light)' : '#fff',
            cursor: disabled ? 'not-allowed' : 'text',
            transition: 'border-color 0.15s, background 0.15s',
            color: 'var(--dark)',
          }}
          value={query}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete="off"
          onChange={handleChange}
          onFocus={() => !disabled && setOpen(true)}
        />
        {/* Matched indicator */}
        {selectedItem && !open && (
          <span style={{
            position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)',
            fontSize: 13, pointerEvents: 'none', opacity: 0.7,
          }}>✓</span>
        )}
        {/* Arrow / clear icon */}
        <span
          style={{ position:'absolute', right:10, top:'50%', transform:'translateY(-50%)',
            cursor:'pointer', fontSize:14, color:'#94a3b8', userSelect:'none' }}
          onMouseDown={e => {
            e.preventDefault();
            if (query) { setQuery(''); onChange('', null); }
            else setOpen(o => !o);
          }}
        >{query ? '✕' : '▾'}</span>
      </div>

      {open && !disabled && (filtered.length > 0 || showManual || showAddNew) && (
        <div style={{
          position:'absolute', top:'calc(100% + 4px)', left:0, right:0, zIndex:9999,
          background:'#fff', border:'1.5px solid #e2e8f0', borderRadius:10,
          boxShadow:'0 8px 28px rgba(0,0,0,0.13)', maxHeight:240, overflowY:'auto',
        }}>
          {filtered.map((item, i) => (
            <div
              key={item._id}
              onMouseEnter={() => setHov(i)}
              onMouseLeave={() => setHov(null)}
              onMouseDown={e => { e.preventDefault(); select(item); }}
              style={{
                padding:'9px 14px', cursor:'pointer',
                background: hov===i ? '#f0fdf4' : item.name.toLowerCase() === query.trim().toLowerCase() ? '#f0fdf4' : '#fff',
                borderBottom:'1px solid #f8fafc',
                display:'flex', alignItems:'center', gap:9,
                transition:'background 0.1s',
              }}
            >
              <span style={{ fontSize:16, flexShrink:0 }}>{icon}</span>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontWeight:600, fontSize:13.5, color:'#1e293b',
                  whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                  {item.name}
                </div>
                {item.routingNumber && (
                  <div style={{ fontSize:11, color:'#94a3b8' }}>Routing: {item.routingNumber}</div>
                )}
                {item.address && (
                  <div style={{ fontSize:11, color:'#94a3b8',
                    whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                    {item.address}
                  </div>
                )}
              </div>
              {renderBadge && renderBadge(item)}
              {item.name.toLowerCase() === query.trim().toLowerCase() && (
                <span style={{ fontSize:12, color:'var(--primary)', fontWeight:700, flexShrink:0 }}>✓</span>
              )}
            </div>
          ))}

          {/* Add to Company Master option */}
          {showAddNew && (
            <div
              onMouseEnter={() => setHov('addnew')}
              onMouseLeave={() => setHov(null)}
              onMouseDown={e => {
                e.preventDefault();
                setQuery(query.trim());
                onChange(query.trim(), null);
                onAddNew(query.trim());
                setOpen(false);
              }}
              style={{
                padding:'9px 14px', cursor:'pointer',
                background: hov==='addnew' ? '#f0fdf4' : '#f8fafc',
                borderTop: filtered.length ? '2px dashed #e2e8f0' : 'none',
                display:'flex', alignItems:'center', gap:9,
              }}
            >
              <span style={{ fontSize:15 }}>➕</span>
              <div>
                <div style={{ fontWeight:700, fontSize:13, color:'var(--primary)' }}>
                  Add "{query.trim()}" to Company Master
                </div>
                <div style={{ fontSize:11, color:'#94a3b8' }}>
                  Save this company for future use
                </div>
              </div>
            </div>
          )}

          {/* Manual entry option (when onAddNew not provided) */}
          {showManual && (
            <div
              onMouseEnter={() => setHov('manual')}
              onMouseLeave={() => setHov(null)}
              onMouseDown={e => { e.preventDefault(); setQuery(query.trim()); onChange(query.trim(), null); setOpen(false); }}
              style={{
                padding:'9px 14px', cursor:'pointer',
                background: hov==='manual' ? '#eff6ff' : '#f8fafc',
                borderTop: filtered.length ? '2px dashed #e2e8f0' : 'none',
                display:'flex', alignItems:'center', gap:9,
              }}
            >
              <span style={{ fontSize:15 }}>✏️</span>
              <div>
                <div style={{ fontWeight:700, fontSize:13, color:'#1e293b' }}>
                  Use: "{query.trim()}"
                </div>
                <div style={{ fontSize:11, color:'#94a3b8' }}>
                  Not in master list — will be used as entered
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
